import { Component, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CredentialsService, OidcAuthService } from 'baseflow-auth';
import { ToastrService } from 'ngx-toastr';
import { Observable, from, of, switchMap } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';

import { WizardStepBaseComponent } from '../';
import { TWizardStepId } from '../../../interfaces';
import { AppSettings } from '../../../models/app-settings.model';
import { AppSettingsService } from '../../../services';

@Component({
    selector: 'app-wizard-step-logged-in',
    templateUrl: './wizard-step-logged-in.component.html',
    styleUrls: ['../base/wizard-step-base.component.scss', './wizard-step-logged-in.component.scss'],
    providers: [{ provide: WizardStepBaseComponent, useExisting: WizardStepLoggedInComponent }],
    imports: [TranslatePipe],
})
export class WizardStepLoggedInComponent extends WizardStepBaseComponent {
    private readonly oidcAuthService = inject(OidcAuthService);
    private readonly credentialsService = inject(CredentialsService);
    private readonly translate = inject(TranslateService);
    private readonly toastr = inject(ToastrService);

    public id: TWizardStepId = 'logged-in';
    public nextButtonLabel = 'toPortal';

    public url: AppSettings['url'];

    protected passwordResetDisabled = false;
    protected logoutDisabled = false;

    public constructor() {
        const appSettings = inject(AppSettingsService);

        super();

        appSettings
            .get(
                tap((settings) => {
                    this.url = settings.url;
                    this.nextPageLink = this.url.portal;
                })
            )
            .subscribe();
    }

    protected submitWizardStep(): Observable<boolean> {
        return of(false);
    }

    protected resetPassword(): void {
        this.passwordResetDisabled = true;
        this.oidcAuthService.user
            .pipe(
                take(1),
                switchMap((user) => from(this.sendPasswordReset(user.id))),
                tap((email) => {
                    this.toastr.success(this.translate.instant('messages.passwordReset', { email }));
                    this.passwordResetDisabled = false;
                }),
                catchError((e) => {
                    console.error(e);

                    this.toastr.error(this.translate.instant('errors.passwordReset'));
                    this.passwordResetDisabled = false;

                    return of();
                })
            )
            .subscribe();
    }

    protected async logout(): Promise<void> {
        this.oidcAuthService.logout();
    }

    private async sendPasswordReset(userId: string): Promise<string | undefined> {
        const credentials = await this.credentialsService.getCredentials(userId);
        await this.credentialsService.resetPassword(userId);
        return credentials.email;
    }
}
